import { Select, TextInput } from '@octano/global-ui';
import { Fragment, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../locales/translations';
import SectionTitle from '../../Text/SectionTitle';

type EmergencyContactPDISectionProps = {
  prefix: string;
  fieldNames?: {
    names: string;
    identificationType: string;
    identification: string;
    passportCountry: string;
    addressStreet: string;
    addressNumber: string;
    addressExtra: string;
    country: string;
    region: string;
    commune: string;
    phone: string;
    cellPhone: string;
    officePhone: string;
  };
};

/**
 * Para usar este componente tu formulario debe estar envuelto en su FormProvider
 * para poder acceder a la información del formulario a través de useFormContext
 * Los nombres agregados en fieldNames se relacionan a los campos que utilizaste
 * al definir tu formulario.
 */
const DynamicEmergencyContactPDI = ({
  prefix,
  fieldNames = {
    names: 'emergencyNames',
    identificationType: 'emergencyIdentificationType',
    identification: 'emergencyIdentification',
    passportCountry: 'emergencyPassportCountry',
    addressStreet: 'emergencyAddressStreet',
    addressNumber: 'emergencyAddressNumber',
    addressExtra: 'emergencyAddressExtra',
    country: 'emergencyCountry',
    region: 'emergencyRegion',
    commune: 'emergencyCommune',
    phone: 'emergencyPhone',
    cellPhone: 'emergencyCellPhone',
    officePhone: 'emergencyOfficePhone',
  },
}: EmergencyContactPDISectionProps) => {
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_FILE_PERSONAL_INFORMATION,
  );
  const { control, watch } = useFormContext();

  const [identificationType] = watch([fieldNames.identificationType]);

  const isPassport = useMemo(() => {
    return identificationType?.value === 'PASSPORT';
  }, [identificationType]);

  const fields = useMemo(() => {
    return (
      <Fragment>
        <Row className="pt-5">
          <Col xs={12}>
            <SectionTitle text={t(`${prefix}.title`)} />
          </Col>
        </Row>
        <Row>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.names}
              label={t(`${prefix}.${fieldNames.names}`)}
              control={control}
              disabled
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <Select
              name={`${fieldNames.identificationType}`}
              label={t(`${prefix}.${fieldNames.identificationType}`)}
              options={[]}
              isClearable={false}
              control={control}
              shouldUnregister={true}
              disabled
            />
          </Col>
          {isPassport && (
            <Col xs={5} lg={4} className="pr-1 pr-md-2">
              <Select
                name={`${fieldNames.passportCountry}`}
                label={t(`${prefix}.${fieldNames.passportCountry}`)}
                options={[]}
                isClearable={false}
                control={control}
                shouldUnregister={true}
                disabled
              />
            </Col>
          )}
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={`${fieldNames.identification}`}
              label={t(`${prefix}.${fieldNames.identification}`)}
              control={control}
              shouldUnregister={true}
              disabled
            />
          </Col>
          <Col className="pb-3" xs={12} lg={8}>
            <TextInput
              name={fieldNames.addressStreet}
              label={t(`${prefix}.${fieldNames.addressStreet}`)}
              control={control}
              disabled
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.addressNumber}
              label={t(`${prefix}.${fieldNames.addressNumber}`)}
              control={control}
              disabled
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.addressExtra}
              label={t(`${prefix}.${fieldNames.addressExtra}`)}
              control={control}
              disabled
            />
          </Col>

          <Col className="pb-3" xs={12} lg={4}>
            <Select
              name={fieldNames.region}
              label={t(`${prefix}.${fieldNames.region}`)}
              options={[]}
              control={control}
              disabled
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <Select
              name={fieldNames.commune}
              label={t(`${prefix}.${fieldNames.commune}`)}
              options={[]}
              control={control}
              shouldUnregister
              disabled
            />
          </Col>

          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.phone}
              label={t(`${prefix}.${fieldNames.phone}`)}
              control={control}
              formatter="phone"
              placeholder={t(`common.placeholder.phone`)}
              disabled
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.cellPhone}
              label={t(`${prefix}.${fieldNames.cellPhone}`)}
              control={control}
              formatter="phone"
              placeholder={t(`common.placeholder.cellphone`)}
              disabled
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.officePhone}
              label={t(`${prefix}.${fieldNames.officePhone}`)}
              control={control}
              formatter="phone"
              placeholder={t(`common.placeholder.officePhone`)}
              disabled
            />
          </Col>
        </Row>
      </Fragment>
    );
  }, [
    isPassport,
    fieldNames.identificationType,
    fieldNames.identification,
    fieldNames.passportCountry,
    fieldNames.addressExtra,
    fieldNames.addressNumber,
    fieldNames.addressStreet,
    fieldNames.region,
    fieldNames.commune,
    fieldNames.phone,
    fieldNames.cellPhone,
    fieldNames.officePhone,
    fieldNames.names,
    t,
    control,
    prefix,
  ]);

  return <>{fields}</>;
};

export default DynamicEmergencyContactPDI;
